import { BottomSheetTextInput } from '@gorhom/bottom-sheet'
import { useMemo } from 'react'
import { Pressable, Text, TextInput, View } from 'react-native'

import { Icon } from '../media/Icon'
import { Typography } from '../text/Typography'
import { asBottomSheetRef } from './asBottomSheetRef'
import type { TInputProps } from './Input.type'
import { useInput } from './useInput'

const ASTERISK = '*'

export const Input = ({ ref, ...props }: TInputProps) => {
  const { startContent, startIcon, endContent, endIcon, isRequired, testID, inBottomSheet = false } = props

  const {
    label,
    description,
    errorMessage,
    getBaseProps,
    getLabelProps,
    getInputWrapperProps,
    getInputProps,
    getMainWrapperProps,
    getHelperWrapperProps,
    getDescriptionProps,
    getErrorMessageProps,
    getIconLeftProps,
    getIconRightProps,
  } = useInput({ ref, ...props })

  // const InputComponent =
  const labelContent = useMemo(
    () =>
      label ? (
        <Typography {...getLabelProps()}>
          {label} {!!isRequired && <Text className="text-danger-text-contrast-default">{ASTERISK}</Text>}
        </Typography>
      ) : null,
    [getLabelProps, isRequired, label],
  )

  const helperWrapper = useMemo(() => {
    if (!errorMessage && !description) return null

    return (
      <View {...getHelperWrapperProps()}>
        {description != null && <Typography {...getDescriptionProps()}>{description}</Typography>}
        {errorMessage != null && (
          <Typography testID={`${testID}-error-message`} {...getErrorMessageProps()}>
            {errorMessage}
          </Typography>
        )}
      </View>
    )
  }, [errorMessage, description, getHelperWrapperProps, getErrorMessageProps, getDescriptionProps, testID])

  const inputWrapper = useMemo(() => {
    const { ref: inputRef, ...inputProps } = getInputProps()

    if (startContent || startIcon || endContent || endIcon) {
      return (
        <View {...getInputWrapperProps()}>
          {startIcon && <Icon name={startIcon} className={getIconLeftProps().className} size="md" />}
          {startContent}

          {inBottomSheet ? (
            <BottomSheetTextInput {...inputProps} ref={asBottomSheetRef(inputRef)} />
          ) : (
            <TextInput {...getInputProps()} />
          )}

          {endContent}
          {endIcon && <Icon className={getIconRightProps().className} name={endIcon} size="md" />}
        </View>
      )
    }

    return (
      <View {...getInputWrapperProps()}>
        {inBottomSheet ? (
          <BottomSheetTextInput {...inputProps} ref={asBottomSheetRef(inputRef)} />
        ) : (
          <TextInput {...getInputProps()} />
        )}
      </View>
    )
  }, [
    inBottomSheet,
    endContent,
    endIcon,
    getIconLeftProps,
    getIconRightProps,
    getInputProps,
    getInputWrapperProps,
    startContent,
    startIcon,
  ])

  const mainWrapper = useMemo(() => {
    return (
      <>
        <Pressable {...getMainWrapperProps()}>
          {labelContent}
          {inputWrapper}
        </Pressable>
        {helperWrapper}
      </>
    )
  }, [getMainWrapperProps, helperWrapper, inputWrapper, labelContent])

  return <View {...getBaseProps()}>{mainWrapper}</View>
}

Input.displayName = 'Input'
